import React, { useState, useEffect } from "react";
import { getVisitMedicines } from "../../../../apis/visit";

interface NursingFormProps {
  initialData?: any;
  visit: { id?: number; visitId?: number } | any;
  onSave: (data: any) => void;
  onCancel: () => void;
}

type Shift = "morning" | "evening" | "night";

const SHIFTS: { key: Shift; label: string; hours: string }[] = [
  { key: "morning", label: "Morning", hours: "6am–2pm" },
  { key: "evening", label: "Evening", hours: "2pm–10pm" },
  { key: "night", label: "Night", hours: "10pm–6am" },
];

const autoDetectShift = (): Shift => {
  const h = new Date().getHours();
  if (h >= 6 && h <= 13) return "morning";
  if (h >= 14 && h <= 21) return "evening";
  return "night";
};

const formatDoseRoute = (instructions: any): string => {
  if (!instructions) return "—";
  const dose = instructions.dose ?? instructions.power
    ? `${instructions.power ?? ""}${instructions.powerUnit ?? instructions.power_unit ?? ""}`.trim() || instructions.dose
    : "";
  const route = instructions.route ?? "";
  return [dose, route].filter(Boolean).join(" / ") || "—";
};

const formatFrequency = (instructions: any): string => {
  if (!instructions) return "—";
  if (instructions.frequency) return instructions.frequency;
  const parts: string[] = [];
  if (instructions.morning) parts.push("Morning");
  if (instructions.afternoon) parts.push("Afternoon");
  if (instructions.evening) parts.push("Evening");
  if (instructions.night) parts.push("Night");
  if (parts.length > 0) return parts.join(" + ");
  if (instructions.type === "sos") return "SOS";
  return "—";
};

const getScheduleBadgeColor = (instructions: any): string => {
  if (!instructions) return "bg-gray-100 text-gray-500";
  const freq = formatFrequency(instructions).toLowerCase();
  if (freq.includes("sos")) return "bg-red-100 text-red-600";
  if (freq.includes("iv") || freq.includes("drip")) return "bg-blue-100 text-blue-600";
  if (freq.includes("night")) return "bg-purple-100 text-purple-600";
  if (freq.includes("morning") || freq.includes("evening")) return "bg-amber-100 text-amber-600";
  return "bg-green-100 text-green-600";
};

const NursingForm: React.FC<NursingFormProps> = ({
  initialData,
  visit,
  onSave,
  onCancel,
}) => {
  const visitId = visit?.id ?? visit?.visitId;

  const [shift, setShift] = useState<Shift>(autoDetectShift());
  const [bp, setBp] = useState(initialData?.vitals?.bp ?? "");
  const [pulse, setPulse] = useState(initialData?.vitals?.pulse ?? "");
  const [temp, setTemp] = useState(initialData?.vitals?.temp ?? "");
  const [spo2, setSpo2] = useState(initialData?.vitals?.spo2 ?? "");
  const [notes, setNotes] = useState(initialData?.notes ?? "");

  const [medicines, setMedicines] = useState<any[]>([]);
  const [loadingMeds, setLoadingMeds] = useState(false);
  const [checkedState, setCheckedState] = useState<Record<number, boolean>>({});

  useEffect(() => {
    if (!visitId) return;
    setLoadingMeds(true);
    getVisitMedicines(visitId)
      .then((res: any) => {
        const data: any[] = res?.data?.data ?? [];
        const active = data.filter((m: any) => m.status === "active");
        setMedicines(active);

        // Pre-fill from initialData if available
        if (initialData?.medicationsAdministered && Array.isArray(initialData.medicationsAdministered)) {
          const preChecked: Record<number, boolean> = {};
          initialData.medicationsAdministered.forEach((entry: any) => {
            if (entry.medicineId != null) {
              preChecked[entry.medicineId] = entry.given ?? false;
            }
          });
          setCheckedState(preChecked);
        }
      })
      .catch(() => {})
      .finally(() => setLoadingMeds(false));
  }, [visitId]);

  const handleCheckChange = (medicineId: number, checked: boolean) => {
    setCheckedState((prev) => ({ ...prev, [medicineId]: checked }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const vitals: any = {};
    if (bp) vitals.bp = bp;
    if (pulse) vitals.pulse = pulse;
    if (temp) vitals.temp = temp;
    if (spo2) vitals.spo2 = spo2;

    onSave({
      shift,
      vitals,
      medicationsAdministered: medicines.map((m: any) => ({
        medicineId: m.medicineId ?? m.id,
        medicineName: m.medicineName,
        given: checkedState[m.medicineId ?? m.id] ?? false,
        instructions: m.instructions,
      })),
      notes,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="p-4 space-y-6">
      {/* SHIFT */}
      <div>
        <h3 className="text-xs font-semibold tracking-widest text-gray-500 uppercase mb-2">Shift</h3>
        <div className="flex gap-2">
          {SHIFTS.map((s) => (
            <button
              key={s.key}
              type="button"
              onClick={() => setShift(s.key)}
              className={`flex flex-col items-center px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                shift === s.key
                  ? "bg-indigo-600 border-indigo-600 text-white"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}
            >
              <span>{s.label}</span>
              <span className={`text-xs mt-0.5 ${shift === s.key ? "text-indigo-200" : "text-gray-400"}`}>
                {s.hours}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* VITALS */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xs font-semibold tracking-widest text-gray-500 uppercase">Vitals</h3>
          <span className="text-xs text-gray-400">Leave blank to skip</span>
        </div>
        <div className="grid grid-cols-4 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">BP (mmHg)</label>
            <input
              type="text"
              value={bp}
              onChange={(e) => setBp(e.target.value)}
              placeholder="e.g. 120/80"
              className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-indigo-400 focus:outline-none focus:ring-1 focus:ring-indigo-200"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Pulse (bpm)</label>
            <input
              type="number"
              value={pulse}
              onChange={(e) => setPulse(e.target.value)}
              placeholder="e.g. 88"
              className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-indigo-400 focus:outline-none focus:ring-1 focus:ring-indigo-200"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Temp (°C)</label>
            <input
              type="text"
              value={temp}
              onChange={(e) => setTemp(e.target.value)}
              placeholder="e.g. 37.2"
              className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-indigo-400 focus:outline-none focus:ring-1 focus:ring-indigo-200"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">SpO2 (%)</label>
            <input
              type="number"
              value={spo2}
              onChange={(e) => setSpo2(e.target.value)}
              placeholder="e.g. 98"
              className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-indigo-400 focus:outline-none focus:ring-1 focus:ring-indigo-200"
            />
          </div>
        </div>
      </div>

      {/* MEDICATIONS ADMINISTERED */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xs font-semibold tracking-widest text-gray-500 uppercase">Medications Administered</h3>
          <span className="text-xs text-gray-400">Tick what was given this shift</span>
        </div>

        {loadingMeds ? (
          <p className="text-sm text-gray-400 py-2">Loading medicines...</p>
        ) : medicines.length === 0 ? (
          <p className="text-sm text-gray-400 py-2">No active medications prescribed.</p>
        ) : (
          <div className="rounded border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase w-2/5">Prescribed Medicine</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Dose / Route</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Schedule</th>
                  <th className="px-3 py-2 text-center text-xs font-semibold text-gray-500 uppercase">Given</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {medicines.map((med: any) => {
                  const medId = med.medicineId ?? med.id;
                  return (
                    <tr key={med.id} className="bg-white hover:bg-gray-50">
                      <td className="px-3 py-2">
                        <span className="font-medium text-gray-900">{med.medicineName}</span>
                      </td>
                      <td className="px-3 py-2 text-gray-700">{formatDoseRoute(med.instructions)}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${getScheduleBadgeColor(med.instructions)}`}>
                          {formatFrequency(med.instructions)}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-center">
                        <input
                          type="checkbox"
                          checked={checkedState[medId] ?? false}
                          onChange={(e) => handleCheckChange(medId, e.target.checked)}
                          className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* NOTES */}
      <div>
        <h3 className="text-xs font-semibold tracking-widest text-gray-500 uppercase mb-2">Notes</h3>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          placeholder="Additional nursing notes..."
          className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-indigo-400 focus:outline-none focus:ring-1 focus:ring-indigo-200"
        />
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
        >
          Save Nursing Entry
        </button>
      </div>
    </form>
  );
};

export default NursingForm;
